import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import Textarea from "../../../../componentes/input/textarea";
import Button from "../../../../componentes/button/button";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import FilialService from "../../../../services/resource/filialService";
import VeiculoService from "../../../../services/resource/veiculoService";
import NovaSolicitacaoLayout from "../shared/NovaSolicitacaoLayout";
import { useRascunhoFormulario } from "../../Rascunho/RascunhoContext";
import { listaDoRascunho, mesclarComPadrao } from "../../../../utils/solicitacoes/rascunhoStorage";
import { enviarAnexosStaged } from "../../../../utils/solicitacoes/anexosStaged";
import { enviarComentariosStaged } from "../../../../utils/solicitacoes/comentariosStaged";
import DateMasked from "../../../../componentes/inputMask/DateMasked";

const filialService = new FilialService();
const veiculoService = new VeiculoService();

const inicial = {
    unidadeId: null,
    veiculoId: null,
    renainf: "",
    dataInfracao: "",
    horaInfracao: "",
    local: "",
    natureza: "",
    valorMulta: "",
    valorBoleto: "",
    dataVencimento: "",
    dataNotificacao: "",
    descricao: "",
};

export default function NovaSolicitacaoMultasForm({ service, etapaInicialExigeAnexo, onCriada, encaminhar }) {
    const [form, setForm] = useState(inicial);
    const [anexos, setAnexos] = useState([]);
    const [comentarios, setComentarios] = useState([]);

    // Rascunho (botão "Salvar rascunho" da página) — só campos, anexos ficam de fora.
    useRascunhoFormulario(
        () => ({ form, comentarios }),
        dados => {
            setForm(mesclarComPadrao(inicial, dados.form));
            setComentarios(listaDoRascunho(dados.comentarios));
        },
    );
    const [unidades, setUnidades] = useState([]);
    const [veiculos, setVeiculos] = useState([]);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        filialService.listarAtivas().then(res => setUnidades(res.data)).catch(() => setUnidades([]));
        veiculoService.listar().then(res => setVeiculos(res.data.filter(v => v.ativo))).catch(() => setVeiculos([]));
    }, []);

    const veiculoSelecionado = veiculos.find(v => v.id === form.veiculoId) || null;

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    function validar() {
        if (!form.unidadeId || !form.veiculoId || !form.renainf.trim() || !form.dataInfracao || !form.horaInfracao
            || !form.local.trim() || !form.natureza.trim() || !form.valorMulta || !form.valorBoleto || !form.dataVencimento) {
            toast.error("Preencha todos os campos obrigatórios.");
            return false;
        }
        if (etapaInicialExigeAnexo && anexos.length === 0) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;

        let usuarioAnalistaId;
        try {
            usuarioAnalistaId = await encaminhar("ETAPA_2_ANALISE");
        } catch {
            return;
        }

        setEnviando(true);
        service.criarSolicitacao({
            ...form,
            usuarioAnalistaId,
            valorMulta: Number(form.valorMulta),
            valorBoleto: Number(form.valorBoleto),
            dataNotificacao: form.dataNotificacao || null,
        })
            .then(async res => {
                const [{ falha: falhaAnexos }, { falha: falhaComentarios }] = await Promise.all([
                    enviarAnexosStaged(res.data.id, anexos),
                    enviarComentariosStaged(res.data.id, comentarios),
                ]);
                if (falhaAnexos.length > 0 || falhaComentarios.length > 0) {
                    const partes = [];
                    if (falhaAnexos.length > 0) partes.push(`${falhaAnexos.length} anexo(s) (${falhaAnexos.map(f => `${f.arquivo.name}: ${f.erro}`).join(", ")})`);
                    if (falhaComentarios.length > 0) partes.push(`${falhaComentarios.length} comentário(s)`);
                    toast.error(`Solicitação criada, mas houve falha ao enviar: ${partes.join(" e ")}.`);
                }
                onCriada(res.data);
            })
            .catch(error => console.error("Erro ao cadastrar multa", error))
            .finally(() => setEnviando(false));
    }

    return (
        <NovaSolicitacaoLayout
            anexos={anexos}
            onAnexosChange={setAnexos}
            comentarios={comentarios}
            onComentariosChange={setComentarios}
            etapaInicialExigeAnexo={etapaInicialExigeAnexo}
            sugestoes={["Detalhe Infração", "Auto de Infração", "Boleto", "Comprovante de Pagamento", "Fotos"]}
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Unidade <span className="text-destructive">*</span></label>
                    <CustomDropdown value={form.unidadeId} onChange={e => set("unidadeId", e.value)} options={unidades} optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                </div>
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Veículo <span className="text-destructive">*</span></label>
                    <CustomDropdown
                        value={form.veiculoId}
                        onChange={e => set("veiculoId", e.value)}
                        options={veiculos}
                        optionValue="id"
                        optionLabel={v => `${v.placa} · ${v.filialNome}${v.codigo ? ` - ${v.codigo}` : ""}`}
                        placeholder="Pesquisar veículo"
                        filter
                    />
                </div>
            </div>

            {veiculoSelecionado && (
                <Input label="Placa" disabled value={veiculoSelecionado.placa} />
            )}

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Input label="RENAINF" required value={form.renainf} onChange={e => set("renainf", e.target.value)} />
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">Natureza <span className="text-destructive">*</span></label>
                    <CustomDropdown
                        value={form.natureza}
                        onChange={e => set("natureza", e.value)}
                        options={["Leve", "Média", "Grave", "Gravíssima"]}
                        placeholder="Selecione Natureza de Infração"
                        optionValue={n => n}
                        optionLabel={n => n}
                    />
                </div>

                <DateMasked type="date" label="Data da infração" required value={form.dataInfracao} onChange={e => set("dataInfracao", e)} />
                <Input type="time" label="Hora" required value={form.horaInfracao} onChange={e => set("horaInfracao", e.target.value)} />
                <Input label="Local" required value={form.local} onChange={e => set("local", e.target.value)} />
                <Input type="number" min="0" step="0.01" label="Valor da multa" required value={form.valorMulta} onChange={e => set("valorMulta", e.target.value)} />
                <Input type="number" min="0" step="0.01" label="Valor do boleto" required value={form.valorBoleto} onChange={e => set("valorBoleto", e.target.value)} />
                <DateMasked type="date" label="Data de vencimento" required value={form.dataVencimento} onChange={e => set("dataVencimento", e)} />
                <DateMasked type="date" label="Data da notificação" value={form.dataNotificacao} onChange={e => set("dataNotificacao", e)} />
            </div>

            <Textarea label="Descrição" value={form.descricao} onChange={e => set("descricao", e.target.value)} rows={3} />

            <div className="flex justify-end">
                <Button variant="blue" label="Cadastrar multa" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </NovaSolicitacaoLayout>
    );
}
